import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authApi, tierApi } from '@/services/api';
import type { Subscription, Invoice } from '@/services/api/auth';
import { toast } from 'react-hot-toast';
import { ConfirmDialog, LoadingSpinner } from '@/components/ui';
import { TierStatusBanner } from '@/components/features/TierStatusBanner';
import { TierSelector } from '@/components/features/TierSelector';
import type { TierStatus } from '@/services/api/tier';
import { TIER_CONFIG } from '@/config/constants';

export function MembershipSettings() {
  const queryClient = useQueryClient();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showSwitchDialog, setShowSwitchDialog] = useState(false);
  const [pendingTierSwitch, setPendingTierSwitch] = useState<'standard' | 'seasonal' | null>(null);

  // Fetch subscription data
  const { data: subscription, isLoading: isLoadingSubscription } = useQuery<Subscription | null>({
    queryKey: ['subscription'],
    queryFn: () => authApi.getSubscription(),
  });

  // Fetch invoices
  const { data: invoices, isLoading: isLoadingInvoices } = useQuery<Invoice[]>({
    queryKey: ['invoices'],
    queryFn: () => authApi.getInvoices(),
  });

  // Fetch tier status
  const { data: tierStatus } = useQuery<TierStatus>({
    queryKey: ['tier-status'],
    queryFn: () => tierApi.getStatus(),
  });

  // Switch tier mutation
  const switchTierMutation = useMutation({
    mutationFn: (newTier: 'standard' | 'seasonal') => tierApi.switchTier(newTier),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['tier-status'] });
      toast.success(data.message);
      setShowSwitchDialog(false);
      setPendingTierSwitch(null);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to switch tier');
    },
  });

  // Refresh spend mutation
  const refreshSpendMutation = useMutation({
    mutationFn: () => tierApi.refreshSpend(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tier-status'] });
      toast.success('Spend data refreshed');
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to refresh spend data');
    },
  });

  // Cancel subscription mutation
  const cancelMutation = useMutation({
    mutationFn: () => authApi.cancelSubscription(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(data.message);
      setShowCancelDialog(false);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to cancel subscription');
    },
  });

  // Resume subscription mutation
  const resumeMutation = useMutation({
    mutationFn: () => authApi.resumeSubscription(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subscription'] });
      toast.success(data.message);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to resume subscription');
    },
  });

  // Create billing portal session
  const portalMutation = useMutation({
    mutationFn: () => authApi.createBillingPortalSession(),
    onSuccess: (data) => {
      window.location.href = (data as { url: string }).url;
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to open billing portal');
    },
  });

  const handleManageBilling = () => {
    portalMutation.mutate();
  };

  const handleTierChange = (tier: 'standard' | 'seasonal') => {
    if (tier !== tierStatus?.tier_type) {
      setPendingTierSwitch(tier);
      setShowSwitchDialog(true);
    }
  };

  const handleConfirmSwitch = () => {
    if (pendingTierSwitch) {
      switchTierMutation.mutate(pendingTierSwitch);
    }
  };

  const handleCancelSwitch = () => {
    setShowSwitchDialog(false);
    setPendingTierSwitch(null);
  };

  const getSwitchMessage = () => {
    if (!pendingTierSwitch || !tierStatus) return '';

    // During grace period, switching is free - no spend requirements
    if (tierStatus.is_within_grace_period) {
      if (pendingTierSwitch === 'seasonal') {
        return 'Switch to Seasonal plan? This plan is best for seasonal advertisers with flexible monthly spend. You can switch freely during your grace period.';
      }
      return 'Switch to Standard plan? This plan is best for year-round advertisers. You can switch freely during your grace period.';
    }

    // After grace period, show spend requirements
    if (pendingTierSwitch === 'seasonal') {
      return `Switching to Seasonal requires $${TIER_CONFIG.SEASONAL_MIN_SPEND_PER_ACCOUNT_YEAR} in ads spend per linked account over the past 12 months. This gives you more flexibility with monthly spend variations.`;
    }
    return `Switching to Standard requires $${TIER_CONFIG.STANDARD_MIN_SPEND_PER_ACCOUNT_30DAYS} in ads spend per linked account over the past 30 days. This is ideal for year-round advertisers.`;
  };

  const handleCancelSubscription = () => {
    cancelMutation.mutate();
  };

  const handleResumeSubscription = () => {
    resumeMutation.mutate();
  };

  // Only show full loading state if we have no tier status yet
  const isLoading = isLoadingSubscription && !tierStatus;
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" variant="primary" label="Loading membership" />
      </div>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'trialing':
        return 'bg-blue-100 text-blue-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-2">Membership</h2>
      <p className="text-gray-600 mb-6">
        Manage your membership plan and view your account status
      </p>

      {/* Tier Status */}
      {tierStatus && (
        <div className="mb-8">
          <TierStatusBanner
            tierStatus={tierStatus}
            onRefresh={() => refreshSpendMutation.mutate()}
          />
        </div>
      )}

      {/* Tier Selection */}
      {tierStatus && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-6 mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Manage Plan</h3>
          <p className="text-gray-600 mb-6">
            Choose between Standard and Seasonal plans based on your advertising needs.{' '}
            {tierStatus.is_within_grace_period && tierStatus.days_until_grace_ends != null ? (
              <>
                You are free to switch plans until{' '}
                {new Date(Date.now() + tierStatus.days_until_grace_ends * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.
                After, switching requires meeting the spend requirement for the new plan.
              </>
            ) : (
              'Switching requires meeting the spend requirement for the new plan.'
            )}
          </p>
          <TierSelector
            selectedTier={tierStatus.tier_type}
            onTierChange={handleTierChange}
            disabled={switchTierMutation.isPending}
          />
        </div>
      )}

      {/* Pro Subscription - Shows when user has an active paid subscription */}
      {subscription && (
        <>
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 md:p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-medium text-gray-900">Pro Subscription</h3>
            <p className="text-gray-600">
              {subscription.plan_name} - ${subscription.monthly_price}/month
            </p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
            {subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
          </span>
        </div>

        <div className="text-sm text-gray-600 mb-4 space-y-1">
          {subscription.trial_end && subscription.status === 'trialing' && (
            <p>Trial ends: {formatDate(subscription.trial_end)}</p>
          )}
          <p>Current period ends: {formatDate(subscription.current_period_end)}</p>
          {subscription.cancel_at_period_end && (
            <p className="text-yellow-600">
              Your subscription will end on {formatDate(subscription.current_period_end)}
            </p>
          )}
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleManageBilling}
            disabled={portalMutation.isPending}
            className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {portalMutation.isPending ? 'Opening...' : 'Manage Billing'}
          </button>

          {subscription.cancel_at_period_end ? (
            <button
              onClick={handleResumeSubscription}
              disabled={resumeMutation.isPending}
              className="px-4 py-2 bg-blue-800 text-white rounded-md hover:bg-blue-900 transition-colors disabled:opacity-50"
            >
              {resumeMutation.isPending ? 'Resuming...' : 'Resume Subscription'}
            </button>
          ) : (
            <button
              onClick={() => setShowCancelDialog(true)}
              className="px-4 py-2 text-red-600 hover:text-red-700 transition-colors"
            >
              Cancel Subscription
            </button>
          )}
        </div>
      </div>

      {/* Billing History */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Billing History</h3>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {isLoadingInvoices ? (
            <div className="p-6 text-center">
              <LoadingSpinner size="md" variant="primary" className="mx-auto" label="Loading invoices" />
            </div>
          ) : invoices && invoices.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(invoice.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {invoice.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${invoice.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded ${
                        invoice.status === 'paid'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {invoice.pdf_url && (
                        <a
                          href={invoice.pdf_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800"
                        >
                          Download
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-500">
              No billing history available
            </div>
          )}
        </div>
      </div>

      {/* Cancel Subscription Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancelSubscription}
        title="Cancel Subscription"
        message="Are you sure you want to cancel your subscription? You will retain access to all features until the end of your current billing period."
        confirmText="Yes, Cancel"
        cancelText="Keep Subscription"
        variant="danger"
        isLoading={cancelMutation.isPending}
      />
        </>
      )}

      {/* Tier Switch Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showSwitchDialog}
        onClose={handleCancelSwitch}
        onConfirm={handleConfirmSwitch}
        title="Switch Plan"
        message={getSwitchMessage()}
        confirmText="Switch Plan"
        cancelText="Cancel"
        variant="warning"
        isLoading={switchTierMutation.isPending}
      />
    </div>
  );
}
